import * as React from 'react';
import {
    FalconUser,
    ICacheManager,
    ICustomerPortalService,
    INotificationService,
    Party,
    Types,
    User,
    EMAIL_REGEX, PHONE_REGEX
} from "@falcon/services";
import {getInputClassName, getModifiedMobileNumber} from "../../CommonFunctions";
import {lazyInject} from "@athena-portal/core";
import {AjaxError} from "rxjs/ajax";
import {StorageSuccessModal} from "../modals/StorageSuccessModal";
import {EditCarMtaSuccessModalWordingGenerator} from "../../pages/dashboard/annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {Overlay} from "@athena-portal/components";

interface IProps {
    party: Party;
    hasIdp: boolean;
}

interface IState {
    editContactDetails: boolean
    editedEmail: string
    editedPhone: string,
    emailValid: boolean,
    phoneValid: boolean,
    invalidEmailMessage: string,
    confirmedEmail: string,
    confirmedPhone: string,
    showSuccessModal: boolean,
    showOverlay: boolean
}

export class ContactDetailsWidget extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.NotificationService)
    private notificationService: INotificationService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    constructor(props: IProps) {
        super(props);

        this.state = {
            editContactDetails: false,
            editedEmail: props.party.email,
            editedPhone: props.party.mobileNumber,
            emailValid: !!props.party.email,
            phoneValid: true,
            invalidEmailMessage: "Invalid email address",
            confirmedEmail: props.party.email,
            confirmedPhone: props.party.mobileNumber,
            showSuccessModal: false,
            showOverlay: false
        }

        this.toggleChangeForm = this.toggleChangeForm.bind(this);
        this.resetContactDetails = this.resetContactDetails.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.updateContactDetails = this.updateContactDetails.bind(this);
        this.closeSuccessModal = this.closeSuccessModal.bind(this);
    }

    public render(): React.ReactNode {
        return (
            <div>
                <div className="account-details-title">
                    <h2 className="inner-content mb-4 dashboard-panel-title">Contact details</h2>
                </div>
                {this.state.editContactDetails ? this.renderEditContactDetails() : this.renderContactDetails()}
                {this.state.showSuccessModal &&
                    <StorageSuccessModal title="All done"
                        explanationWording={<p>Your contact details have been changed and saved</p>}
                        anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                        explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}
                        onClose={this.closeSuccessModal}
                    />
                }
            </div>
        )
    }

    private renderContactDetails(): React.ReactNode {
        return (
            <>
                <div className="panel-detail-output-entity">
                    <label className="account-details phone font-weight-bold">Phone number:</label>
                    {getModifiedMobileNumber(this.state.confirmedPhone)}
                </div>
                <div className="panel-detail-output-entity">
                    <label className="account-details email font-weight-bold">Email:</label>
                    {this.state.confirmedEmail}
                </div>
                <div className="form-group pt-2">
                    <button type="button"
                        className="btn btn-white-dashboard m-auto"
                        onClick={this.toggleChangeForm}
                        id="editContactDetails"
                        disabled={this.props.hasIdp === undefined}>
                        Edit contact
                    </button>
                </div>
            </>
        )
    }

    private renderEditContactDetails(): React.ReactNode {
        return (
            <>
                <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <div className="input-group m-auto">
                        <input type="text" className={getInputClassName(this.state.emailValid)} id="email" value={this.state.editedEmail} onChange={this.handleChange} readOnly={this.props.hasIdp}/>
                        {!this.state.emailValid ? this.renderErrorMessage(this.state.invalidEmailMessage) : ""}
                    </div>
                </div>
                <div className="form-group padding-top-30">
                    <label htmlFor="email">Phone number</label>
                    <div className="input-group m-auto">
                        <input type="text" className={getInputClassName(this.state.phoneValid)} id="phone" value={this.state.editedPhone} onChange={this.handleChange}/>
                        {!this.state.phoneValid ? this.renderErrorMessage("Invalid UK mobile phone number") : ""}
                    </div>
                </div>
                <div className="form-group padding-top-30">
                    <button type="button"
                        className="btn btn-primary btn-blue-dashboard m-auto"
                        onClick={this.updateContactDetails}
                        id="updateContactDetails"
                        disabled={!this.contactDetailsChanged()}>
                        Save changes
                    </button>
                </div>
                <div className="form-group">
                    <button type="button"
                        className="btn btn-white-dashboard m-auto"
                        onClick={this.resetContactDetails}
                        id="cancelChanges">
                        Cancel changes
                    </button>
                </div>
                <Overlay show={this.state.showOverlay}/>
            </>
        )
    }

    private renderErrorMessage(message: string): React.ReactNode {
        return (
            <div className="invalid-feedback d-block">{message}</div>
        )
    }

    private toggleChangeForm(): void {
        this.setState({
            editContactDetails: !this.state.editContactDetails
        })
    }

    private resetContactDetails(): void {
        this.setState({
            editContactDetails: !this.state.editContactDetails,
            editedEmail: this.state.confirmedEmail,
            editedPhone: this.state.confirmedPhone
        })
    }

    private handleChange(event: any): void {
        event.preventDefault();
        const { id, value } = event.target;

        switch (id) {
            case "email":
                this.setState({
                    editedEmail: value,
                    emailValid: EMAIL_REGEX.test(value),
                });
                break;
            case "phone":
                this.setState({
                    editedPhone: value,
                    phoneValid: PHONE_REGEX.test(value),
                });
                break;
            default:
                break;
        }
    }

    private updateContactDetails(): void {
        if (this.state.emailValid && this.state.phoneValid) {
            this.setState({
                showOverlay: true
            });

            const user: FalconUser = {
                email: this.state.editedEmail,
                mobile: this.state.editedPhone,
                firstName: this.props.party.firstName,
                lastName: this.props.party.lastName
            };

            let oldEmail: string = "";
            this.customerPortalService.getUserById().subscribe((output: User) => {
                oldEmail = output.email;
            });

            this.customerPortalService.updateUser(user)
                .subscribe(
                    (output: any) => {
                        if (output instanceof AjaxError && user.email !== oldEmail) {
                            if (output.status === 409) {
                                this.setState({
                                    emailValid: false,
                                    invalidEmailMessage: "Unable to update email address. Please try another email.",
                                    showOverlay: false
                                });
                            } else {
                                this.notificationService.push({
                                    level: "ERROR",
                                    message: "We are experiencing some technical issues, please come back later"
                                });
                            }
                        } else {
                            this.toggleChangeForm();

                            this.setState({
                                confirmedEmail: this.state.editedEmail,
                                confirmedPhone: this.state.editedPhone,
                                showSuccessModal: true,
                                showOverlay: false
                            })

                            // Clear cache
                            this.cacheManager.clearCache("getPolicies");
                        }
                    },
                    () => {
                        this.setState({
                            showOverlay: false
                        });

                        console.log("Something went wrong");
                    });
        }
    }

    private contactDetailsChanged(): boolean {
        return this.state.editedEmail !== this.props.party.email || this.state.editedPhone !== this.props.party.mobileNumber;
    }

    private closeSuccessModal(): void {
        this.setState({
            showSuccessModal: false
        })
    }
}

